<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { WorkspaceSubheadingReorderDirection } from '@/services/workspaceService'
import type { CreateOrUpdateHeadingRequest, HeadingDTO } from '@/types/heading'
import type { WorkspaceDTO } from '@/types/workspace'

import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import {
  createWorkspaceSubheading,
  deleteWorkspaceSubheading,
  getWorkspaceSubheadings,
  reorderWorkspaceSubheading,
  updateWorkspaceSubheading,
} from '@/services/workspaceService'

const props = defineProps<{
  workspace: WorkspaceDTO
}>()

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const { t, locale } = useI18n()
const toast = useAppToast()

// State
const subheadings = ref<HeadingDTO[]>([])
const loading = ref(false)
const dialogVisible = ref(false)
const editMode = ref(false)
const editingSubheadingId = ref<number | null>(null)
const editingHasChildren = ref(false)
const editingDepth = ref(0)
const expandedKeys = ref<Record<string, boolean>>({})
const reorderingId = ref<number | null>(null)

interface SubheadingTreeNode {
  key: string
  data: HeadingDTO & { depth: number }
  children?: SubheadingTreeNode[]
}

// Form data - now includes parentId for nested subheadings
const subheadingForm = ref<CreateOrUpdateHeadingRequest>({
  nameDE: '',
  nameEN: '',
  abbreviation: '',
  order: 0,
  parentId: null,
})

// Check if workspace has a heading assigned
const hasHeading = computed(() => !!props.workspace.headingId)

// Get localized heading name
const headingName = computed(() => {
  if (!props.workspace.heading) {
    return ''
  }
  return locale.value.startsWith('de')
    ? props.workspace.heading.nameDE
    : props.workspace.heading.nameEN
})

// Convert subheadings to TreeNode format for TreeTable
// Depth 0 = direct child of workspace heading (depth 1 in global hierarchy)
// Depth 1 = grandchild (depth 2 in global hierarchy, max allowed)
function headingToTreeNode(heading: HeadingDTO, depth: number): SubheadingTreeNode {
  const sortedChildren = heading.subHeadings
    ? [...heading.subHeadings].sort((a, b) => a.order - b.order)
    : []

  return {
    key: String(heading.id),
    data: {
      ...heading,
      depth,
    },
    children: sortedChildren.map((sub) => headingToTreeNode(sub, depth + 1)),
  }
}

const treeNodes = computed<SubheadingTreeNode[]>(() => {
  const sorted = [...subheadings.value].sort((a, b) => a.order - b.order)
  return sorted.map((h) => headingToTreeNode(h, 0))
})
const columns = computed<TableColumn<SubheadingTreeNode>[]>(() => [
  { id: 'order', header: t('pages.workspaces.subheadings.fields.order') },
  { id: 'abbreviation', header: t('pages.workspaces.subheadings.fields.abbreviation') },
  { id: 'nameDE', header: t('pages.workspaces.subheadings.fields.nameDE') },
  { id: 'nameEN', header: t('pages.workspaces.subheadings.fields.nameEN') },
  { id: 'actions', header: t('common.actions.actions') },
])

// Available parents for creating nested subheadings
// - null = create as direct child of workspace heading
// - depth 0 subheading = create as grandchild (depth 1 in local view)
// Cannot select depth 1 subheadings as that would exceed max depth
interface ParentOption {
  id: number | null
  label: string
  depth: number
}

const availableParents = computed<ParentOption[]>(() => {
  const result: ParentOption[] = []

  // Option to create directly under the workspace's heading
  const headingLabel = props.workspace.heading
    ? `${props.workspace.heading.abbreviation} - ${locale.value.startsWith('de') ? props.workspace.heading.nameDE : props.workspace.heading.nameEN}`
    : t('pages.workspaces.subheadings.parentHeading')
  result.push({
    id: null, // null = direct child of workspace heading
    label: headingLabel,
    depth: -1, // Marker for root
  })

  // Add depth 0 subheadings as possible parents (creating depth 1 children)
  for (const sub of subheadings.value) {
    // Exclude the subheading being edited
    if (editingSubheadingId.value !== null && sub.id === editingSubheadingId.value) {
      continue
    }
    result.push({
      id: sub.id,
      label: `${headingLabel} > ${sub.abbreviation} - ${locale.value.startsWith('de') ? sub.nameDE : sub.nameEN}`,
      depth: 0,
    })
  }

  return result
})

// Lifecycle
onMounted(() => {
  if (hasHeading.value) {
    loadSubheadings()
  }
})

// Methods
async function loadSubheadings() {
  try {
    loading.value = true
    subheadings.value = await getWorkspaceSubheadings(props.workspace.id)
  } catch (error) {
    console.error('Error loading subheadings:', error)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.workspaces.subheadings.messages.loadError'),
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

function getDefaultOrder(parentId: number | null): number {
  if (parentId === null) {
    // Direct child of workspace heading - find max order among direct subheadings
    const maxOrder =
      subheadings.value.length > 0 ? Math.max(...subheadings.value.map((s) => s.order)) : 0
    return maxOrder + 1
  }

  // Find the parent subheading and get max order among its children
  const parent = subheadings.value.find((s) => s.id === parentId)
  if (parent && parent.subHeadings && parent.subHeadings.length > 0) {
    return Math.max(...parent.subHeadings.map((s) => s.order), 0) + 1
  }
  return 1
}

function openCreateDialog(parentId: number | null = null) {
  editMode.value = false
  editingSubheadingId.value = null
  editingHasChildren.value = false
  editingDepth.value = parentId !== null ? 1 : 0

  subheadingForm.value = {
    nameDE: '',
    nameEN: '',
    abbreviation: '',
    order: getDefaultOrder(parentId),
    parentId,
  }
  dialogVisible.value = true
}

function openEditDialog(node: SubheadingTreeNode) {
  const subheading = node.data
  editMode.value = true
  editingSubheadingId.value = subheading.id
  editingDepth.value = subheading.depth
  editingHasChildren.value = (subheading.subHeadings?.length ?? 0) > 0

  subheadingForm.value = {
    nameDE: subheading.nameDE,
    nameEN: subheading.nameEN,
    abbreviation: subheading.abbreviation,
    order: subheading.order,
    parentId: subheading.parentId === props.workspace.headingId ? null : subheading.parentId,
  }
  dialogVisible.value = true
}

async function saveSubheading() {
  try {
    loading.value = true

    if (editMode.value && editingSubheadingId.value) {
      // Update existing subheading
      await updateWorkspaceSubheading(
        props.workspace.id,
        editingSubheadingId.value,
        subheadingForm.value,
      )

      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.workspaces.subheadings.messages.updateSuccess'),
        duration: 3000,
      })
    } else {
      // Create new subheading
      await createWorkspaceSubheading(props.workspace.id, subheadingForm.value)

      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.workspaces.subheadings.messages.createSuccess'),
        duration: 3000,
      })
    }

    dialogVisible.value = false
    await loadSubheadings()
  } catch (error: any) {
    console.error('Error saving subheading:', error)
    const errorMessage =
      error.response?.data || t('pages.workspaces.subheadings.messages.saveError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

async function confirmDelete(node: SubheadingTreeNode) {
  const subheading = node.data
  const hasChildren = (subheading.subHeadings?.length ?? 0) > 0

  let message = t('pages.workspaces.subheadings.dialog.confirmDelete', {
    name: subheading.abbreviation,
  })
  if (hasChildren) {
    message += ` ${t('pages.workspaces.subheadings.dialog.hasChildrenWarning')}`
  }

  const accepted = await appConfirm({
    message,
    title: t('common.actions.delete'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.delete'),
    confirmColor: 'error',
  })
  if (accepted) await deleteSubheadingConfirmed(subheading.id)
}

async function deleteSubheadingConfirmed(subheadingId: number) {
  try {
    loading.value = true
    await deleteWorkspaceSubheading(props.workspace.id, subheadingId)

    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.workspaces.subheadings.messages.deleteSuccess'),
      duration: 3000,
    })

    await loadSubheadings()
  } catch (error: any) {
    console.error('Error deleting subheading:', error)
    const errorDetail = error.response?.data?.includes('events')
      ? t('pages.workspaces.subheadings.messages.hasEvents')
      : t('pages.workspaces.subheadings.messages.deleteError')

    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorDetail,
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

function closeDialog() {
  dialogVisible.value = false
  editMode.value = false
  editingSubheadingId.value = null
  editingHasChildren.value = false
  editingDepth.value = 0
}

// Check if a subheading is first among its siblings
function isFirstAmongSiblings(node: SubheadingTreeNode): boolean {
  const subheading = node.data

  // Find siblings (subheadings with same parent)
  let siblings: HeadingDTO[]
  if (subheading.depth === 0) {
    // Direct children of workspace heading
    siblings = subheadings.value
  } else {
    // Find the parent and get its children
    const parent = subheadings.value.find((s) => s.id === subheading.parentId)
    siblings = parent?.subHeadings ?? []
  }

  if (siblings.length === 0) {
    return true
  }

  const sorted = [...siblings].sort((a, b) => a.order - b.order)
  return sorted[0]?.id === subheading.id
}

// Check if a subheading is last among its siblings
function isLastAmongSiblings(node: SubheadingTreeNode): boolean {
  const subheading = node.data

  // Find siblings (subheadings with same parent)
  let siblings: HeadingDTO[]
  if (subheading.depth === 0) {
    // Direct children of workspace heading
    siblings = subheadings.value
  } else {
    // Find the parent and get its children
    const parent = subheadings.value.find((s) => s.id === subheading.parentId)
    siblings = parent?.subHeadings ?? []
  }

  if (siblings.length === 0) {
    return true
  }

  const sorted = [...siblings].sort((a, b) => a.order - b.order)
  return sorted[sorted.length - 1]?.id === subheading.id
}

// Reorder a subheading up or down
async function reorderSubheading(
  node: SubheadingTreeNode,
  direction: WorkspaceSubheadingReorderDirection,
) {
  const subheading = node.data
  reorderingId.value = subheading.id

  try {
    await reorderWorkspaceSubheading(props.workspace.id, subheading.id, direction)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.workspaces.subheadings.messages.reorderSuccess'),
      duration: 2000,
    })
    await loadSubheadings()
  } catch (err) {
    console.error('Error reordering subheading:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.workspaces.subheadings.messages.reorderError'),
      duration: 5000,
    })
  } finally {
    reorderingId.value = null
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">
          {{ t('pages.workspaces.subheadings.title') }}
        </h1>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {{ t('pages.workspaces.subheadings.subtitle') }}
        </p>
      </div>
      <UButton
        v-if="hasHeading"
        :label="t('pages.workspaces.subheadings.actions.create')"
        icon="i-lucide-plus"
        @click="openCreateDialog()"
      />
    </div>

    <!-- No heading assigned message -->
    <AppAlert v-if="!hasHeading" color="info" :closable="false">
      {{ t('pages.workspaces.subheadings.noHeadingAssigned') }}
    </AppAlert>

    <!-- Content when heading is assigned -->
    <template v-else>
      <!-- Parent heading info -->
      <div class="mycard p-4">
        <div class="flex items-center gap-3">
          <UIcon name="i-lucide-bookmark" class="text-xl text-[var(--app-text-primary)]" />
          <div>
            <span class="text-sm text-gray-500 dark:text-gray-400">
              {{ t('pages.workspaces.subheadings.parentHeading') }}
            </span>
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
              {{ headingName }}
              <span class="text-gray-500">({{ workspace.heading?.abbreviation }})</span>
            </h3>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!loading && treeNodes.length === 0" class="py-12 text-center">
        <div class="text-gray-500 dark:text-gray-400">
          <UIcon name="i-lucide-list" class="mb-4 block text-4xl" />
          <p class="mb-2 text-lg font-medium">
            {{ t('pages.workspaces.subheadings.empty.title') }}
          </p>
          <p class="mb-4 text-sm">
            {{ t('pages.workspaces.subheadings.empty.subtitle') }}
          </p>
        </div>
      </div>

      <!-- TreeTable for subheadings -->
      <div
        v-else
        class="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100 shadow-sm dark:border-neutral-700 dark:bg-neutral-900"
      >
        <AppDataTableShell
          v-model:expanded="expandedKeys"
          :data="treeNodes"
          :columns="columns"
          :loading="loading"
          :get-row-id="(node: SubheadingTreeNode) => node.key"
          :get-sub-rows="(node: SubheadingTreeNode) => node.children"
        >
          <template #order-cell="{ row }">
            <span class="text-sm text-gray-900 dark:text-gray-100">
              {{ row.original.data.order }}
            </span>
          </template>
          <template #abbreviation-cell="{ row }">
            <div class="flex items-center gap-1" :style="{ paddingLeft: `${row.depth}rem` }">
              <UButton
                v-if="row.getCanExpand()"
                :icon="row.getIsExpanded() ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                color="neutral"
                size="xs"
                variant="ghost"
                :aria-label="row.original.data.abbreviation"
                @click.stop="row.toggleExpanded()"
              />
              <span v-else class="w-7 shrink-0" aria-hidden="true" />
              <UBadge :label="row.original.data.abbreviation" color="neutral" />
            </div>
          </template>
          <template #nameDE-cell="{ row }">
            <div
              class="text-sm font-medium text-gray-900 dark:text-gray-100"
              :class="{ 'font-semibold': row.original.data.depth === 0 }"
            >
              {{ row.original.data.nameDE }}
            </div>
          </template>
          <template #nameEN-cell="{ row }">
            <div class="text-sm text-gray-600 dark:text-gray-400">
              {{ row.original.data.nameEN }}
            </div>
          </template>
          <template #actions-cell="{ row }">
            <div class="flex gap-1">
              <!-- Move up button -->
              <UButton
                v-app-tooltip.top="t('pages.workspaces.subheadings.actions.moveUp')"
                icon="i-lucide-arrow-up"
                color="neutral"
                size="sm"
                :disabled="isFirstAmongSiblings(row.original) || reorderingId !== null"
                :loading="reorderingId === row.original.data.id"
                :aria-label="t('pages.workspaces.subheadings.actions.moveUp')"
                @click.stop="reorderSubheading(row.original, 'Up')"
                variant="ghost"
              />
              <!-- Move down button -->
              <UButton
                v-app-tooltip.top="t('pages.workspaces.subheadings.actions.moveDown')"
                icon="i-lucide-arrow-down"
                color="neutral"
                size="sm"
                :disabled="isLastAmongSiblings(row.original) || reorderingId !== null"
                :loading="reorderingId === row.original.data.id"
                :aria-label="t('pages.workspaces.subheadings.actions.moveDown')"
                @click.stop="reorderSubheading(row.original, 'Down')"
                variant="ghost"
              />
              <!-- Add nested subheading button (only for depth 0) -->
              <UButton
                v-if="row.original.data.depth === 0"
                v-app-tooltip.top="t('pages.workspaces.subheadings.actions.addNested')"
                icon="i-lucide-plus"
                color="neutral"
                size="sm"
                :aria-label="t('pages.workspaces.subheadings.actions.addNested')"
                @click.stop="openCreateDialog(row.original.data.id)"
                variant="ghost"
              />
              <UButton
                icon="i-lucide-pencil"
                color="neutral"
                size="sm"
                :aria-label="t('common.actions.edit')"
                @click.stop="openEditDialog(row.original)"
                variant="ghost"
              />
              <UButton
                icon="i-lucide-trash"
                color="error"
                size="sm"
                :aria-label="t('common.actions.delete')"
                @click.stop="confirmDelete(row.original)"
                variant="ghost"
              />
            </div>
          </template>
          <template #empty>
            <div class="py-12 text-center">
              <div class="text-gray-500 dark:text-gray-400">
                <UIcon name="i-lucide-list" class="mb-4 block text-4xl" />
                <p class="mb-2 text-lg font-medium">
                  {{ t('pages.workspaces.subheadings.empty.title') }}
                </p>
                <p class="mb-4 text-sm">
                  {{ t('pages.workspaces.subheadings.empty.subtitle') }}
                </p>
              </div>
            </div>
          </template>
        </AppDataTableShell>
      </div>
    </template>

    <!-- Create/Edit Dialog -->
    <AppModal
      v-model:open="dialogVisible"
      :title="
        editMode
          ? t('pages.workspaces.subheadings.dialog.titleEdit')
          : t('pages.workspaces.subheadings.dialog.titleCreate')
      "
      modal
      class="w-[90vw] max-w-xl"
      @hide="closeDialog"
    >
      <form class="space-y-6" @submit.prevent="saveSubheading">
        <div class="space-y-4">
          <!-- Parent Selector -->
          <div>
            <label for="parentId" class="mb-2 block text-sm font-medium">
              {{ t('pages.workspaces.subheadings.fields.parent') }}
            </label>
            <AppSelect
              id="parentId"
              v-model="subheadingForm.parentId"
              :options="availableParents"
              option-value="id"
              option-label="label"
              :placeholder="t('pages.workspaces.subheadings.fields.parentPlaceholder')"
              :disabled="loading || editingHasChildren"
              class="w-full"
            />
            <small v-if="editingHasChildren" class="text-orange-700 dark:text-orange-400">
              {{ t('pages.workspaces.subheadings.fields.parentDisabledHasChildren') }}
            </small>
            <small v-else class="text-gray-500 dark:text-gray-400">
              {{ t('pages.workspaces.subheadings.fields.parentHelp') }}
            </small>
          </div>

          <!-- Abbreviation -->
          <div>
            <label for="abbreviation" class="mb-2 block text-sm font-medium">
              {{ t('pages.workspaces.subheadings.fields.abbreviation') }}
              <span class="text-red-700 dark:text-red-400">*</span>
            </label>
            <UInput
              id="abbreviation"
              v-model="subheadingForm.abbreviation"
              :placeholder="t('pages.workspaces.subheadings.fields.abbreviationPlaceholder')"
              required
              class="w-full"
            />
            <small class="text-gray-500 dark:text-gray-400">
              {{ t('pages.workspaces.subheadings.fields.abbreviationHelp') }}
            </small>
          </div>

          <!-- German Name -->
          <div>
            <label for="nameDE" class="mb-2 block text-sm font-medium">
              {{ t('pages.workspaces.subheadings.fields.nameDE') }}
              <span class="text-red-700 dark:text-red-400">*</span>
            </label>
            <UInput
              id="nameDE"
              v-model="subheadingForm.nameDE"
              :placeholder="t('pages.workspaces.subheadings.fields.nameDEPlaceholder')"
              required
              class="w-full"
            />
          </div>

          <!-- English Name -->
          <div>
            <label for="nameEN" class="mb-2 block text-sm font-medium">
              {{ t('pages.workspaces.subheadings.fields.nameEN') }}
              <span class="text-red-700 dark:text-red-400">*</span>
            </label>
            <UInput
              id="nameEN"
              v-model="subheadingForm.nameEN"
              :placeholder="t('pages.workspaces.subheadings.fields.nameENPlaceholder')"
              required
              class="w-full"
            />
          </div>

          <!-- Order -->
          <div>
            <label for="order" class="mb-2 block text-sm font-medium">
              {{ t('pages.workspaces.subheadings.fields.order') }}
              <span class="text-red-700 dark:text-red-400">*</span>
            </label>
            <UInputNumber
              id="order"
              v-model="subheadingForm.order"
              :min="0"
              class="w-full"
              :increment="false"
              :decrement="false"
            />
            <small class="text-gray-500 dark:text-gray-400">
              {{ t('pages.workspaces.subheadings.fields.orderHelp') }}
            </small>
          </div>
        </div>

        <!-- Dialog Actions -->
        <div
          class="flex justify-end space-x-3 border-t border-neutral-200 pt-4 dark:border-neutral-700"
        >
          <UButton
            :label="t('common.actions.cancel')"
            color="neutral"
            @click="closeDialog"
            variant="outline"
          />
          <UButton
            :label="editMode ? t('common.actions.update') : t('common.actions.create')"
            type="submit"
            :loading="loading"
          />
        </div>
      </form>
    </AppModal>
  </div>
</template>
